/** A strip of tabs: the one visual every place and every record uses - ADR-0212.
 *
 * **These are links, not an ARIA tablist**, for the reason `shell/SectionTabs.tsx` gives: each tab is
 * a different address and a different screen, and the tab pattern promises one page of panels that
 * swap in place. A strip of `role="tab"` that navigates breaks that promise for a screen reader. This
 * is a `nav` landmark of links with `aria-current="page"` on the one you are on. (A page whose panels
 * *do* swap in place - the record page's five - uses `RecordTabs`, the product's real tablist.)
 *
 * It only draws. What a tab is a link to is the caller's (`renderLink`), so the shell can use the
 * router's `Link` (which carries the reporting window across) and a test can use a plain anchor. With
 * no `renderLink`, a tab is a button that calls `onSelect`. A strip with fewer than two tabs draws
 * nothing: a strip with one entry is a heading in a costume.
 */

import type { ReactNode } from "react";

import styles from "./SectionTabs.module.css";

export interface SectionTabItem {
  key: string;
  label: string;
  current: boolean;
}

/** What a tab's element gets, so every renderer draws it the same. */
export interface SectionTabLinkProps {
  className: string;
  "aria-current": "page" | undefined;
  "data-active": boolean;
  children: ReactNode;
}

export function SectionTabs({
  tabs,
  label,
  renderLink,
  onSelect,
  testId,
}: {
  tabs: readonly SectionTabItem[];
  /** The landmark's name: "Processing pages". */
  label: string;
  renderLink?: (tab: SectionTabItem, props: SectionTabLinkProps) => ReactNode;
  onSelect?: (key: string) => void;
  testId?: string;
}) {
  if (tabs.length < 2) return null;
  return (
    <nav className={styles.strip} aria-label={label} data-testid={testId}>
      {tabs.map((tab) => {
        const props: SectionTabLinkProps = {
          className: styles.tab ?? "",
          "aria-current": tab.current ? "page" : undefined,
          "data-active": tab.current,
          children: tab.label,
        };
        if (renderLink) return <span key={tab.key} className={styles.item}>{renderLink(tab, props)}</span>;
        return (
          <span key={tab.key} className={styles.item}>
            <button type="button" {...props} onClick={() => onSelect?.(tab.key)} />
          </span>
        );
      })}
    </nav>
  );
}
